import { Button, Checkbox, Modal } from "@bp/ui-components";
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "urql";
import { getFragmentData, graphql } from "../../../client/gql";
import { showSuccessToast, showUserErrorToast } from "../../../utils/toast";
import css from "../orgList.module.scss";

// Colocated fragment: the application fields this license editor needs.
const LicenseModal_Application = graphql(`
  fragment LicenseModal_Application on Application {
    uuid
    name
    licensedOrganizations {
      uuid
    }
    accessGrantedProfiles {
      uuid
    }
  }
`);

const LicenseModalQuery = graphql(`
  query LicenseModal_Query {
    applications(options: { sort: { name: ASC } }) {
      ...LicenseModal_Application
    }
  }
`);

const LicenseModalUpdateApplication = graphql(`
  mutation LicenseModal_UpdateApplication(
    $where: ApplicationWhere!
    $update: ApplicationUpdateInput!
  ) {
    updateApplications(where: $where, update: $update) {
      applications {
        uuid
      }
    }
  }
`);

type Props = {
  org: { uuid: string; name: string };
  profile: { uuid: string; name: string };
  onClose: () => void;
  onSaved: () => void;
};

/**
 * Grant/revoke a member's access to the products the organization is licensed
 * for. Each toggled app is a single `pimUpdateApplication` connect/disconnect on
 * `accessGrantedProfiles` (same pattern as ApplicationDetailPage).
 */
export function LicenseModal({ org, profile, onClose, onSaved }: Props) {
  const [{ data }] = useQuery({ query: LicenseModalQuery });
  const [, updateApplication] = useMutation(LicenseModalUpdateApplication);
  const [busy, setBusy] = useState(false);

  // Only apps the org holds a license for are meaningful to assign.
  const apps = useMemo(
    () =>
      getFragmentData(LicenseModal_Application, data?.applications ?? []).filter((a) =>
        (a.licensedOrganizations ?? []).some((o) => o.uuid === org.uuid),
      ),
    [data, org.uuid],
  );
  const initial = useMemo(() => {
    const s = new Set<string>();
    for (const a of apps) {
      if ((a.accessGrantedProfiles ?? []).some((p) => p.uuid === profile.uuid)) {
        s.add(a.uuid);
      }
    }
    return s;
  }, [apps, profile.uuid]);

  const [edited, setEdited] = useState<Set<string> | null>(null);
  const current = edited ?? initial;
  const toggle = (uuid: string) =>
    setEdited((prev) => {
      const next = new Set(prev ?? initial);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });

  const save = async () => {
    setBusy(true);
    const changed = apps.filter((a) => current.has(a.uuid) !== initial.has(a.uuid));
    for (const a of changed) {
      const grant = current.has(a.uuid);
      const update = grant
        ? { accessGrantedProfiles: [{ connect: [{ where: { node: { uuid: profile.uuid } } }] }] }
        : {
            accessGrantedProfiles: [{ disconnect: [{ where: { node: { uuid: profile.uuid } } }] }],
          };
      const res = await updateApplication({ where: { uuid: a.uuid }, update });
      if (res.error) {
        setBusy(false);
        showUserErrorToast({ text: "Speichern fehlgeschlagen", error: res.error });
        return;
      }
    }
    setBusy(false);
    showSuccessToast(`Lizenzen für ${profile.name} aktualisiert`);
    onSaved();
  };

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      hideFooter={false}
      width="s"
      title={`Lizenzen — ${profile.name}`}
      onRequestClose={onClose}
      footer={
        <div className={css.foot}>
          <span className={css.footHint}>Gilt für lizenzierte Produkte von {org.name}.</span>
          <div className={css.footActions}>
            <Button hierarchy="tertiary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              hierarchy="primary"
              disabled={busy || apps.length === 0}
              onClick={() => void save()}
            >
              Speichern
            </Button>
          </div>
        </div>
      }
    >
      <div className={css.stack}>
        {apps.length === 0 && (
          <span style={{ fontSize: 13, color: "var(--color-grey)" }}>
            {org.name} hat keine lizenzierten Produkte.
          </span>
        )}
        {apps.map((a) => (
          <Checkbox
            key={a.uuid}
            name={`license-${a.uuid}`}
            label={a.name}
            checked={current.has(a.uuid)}
            onChange={() => toggle(a.uuid)}
          />
        ))}
      </div>
    </Modal>
  );
}
